/**
 * timeframeAlignment — classify a single timeframe's stance from its bars.
 *
 * Used by the Live Coach Feed multi-TF alignment pills. Each pill shows
 * one timeframe's current stance at-a-glance:
 *
 *   🟢 BULLISH    — EMA stack pointing up AND price above the shortest EMA
 *   🔴 BEARISH    — EMA stack pointing down AND price below the shortest EMA
 *   🟡 MIXED      — anything in between (chop, crossover, indecisive)
 *   ⚪ UNKNOWN    — bars unavailable or insufficient warmup
 *
 * The rule is intentionally simple — two conditions, both must hold for
 * a definitive call. This matches the way most traders look at a chart:
 * "are the EMAs stacked? is price on the right side?" If yes to both →
 * trend is intact. Anything else → don't bet on continuation.
 *
 * Pure function. No side effects. Easy to unit test.
 */

import { computeEmas } from './emas.js';

export const STANCE = Object.freeze({
  BULLISH:  'bullish',
  BEARISH:  'bearish',
  MIXED:    'mixed',
  UNKNOWN:  'unknown',
});

// Default EMA periods for the stack check. 9/21/50 is the standard 3-EMA
// system used elsewhere in the journal (atmBarContext stack classifier,
// setupPatterns Holy Grail detector). Keeping it the same here so users
// see a consistent "what counts as the trend?" definition across surfaces.
const DEFAULT_PERIODS = [9, 21, 50];

/**
 * Classify the trend stance of a single timeframe.
 *
 * @param {Array}  bars                 Bars[] ascending — needs at least
 *                                       max(periods) bars for full warmup,
 *                                       otherwise returns 'unknown'.
 * @param {Object} [opts]
 * @param {number[]} [opts.emaPeriods]  EMA periods to stack. Default [9,21,50].
 *                                       Must be at least 2 periods.
 * @returns {string} One of STANCE.* values
 */
export const classifyTimeframeStance = (bars, opts = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return STANCE.UNKNOWN;

  // Resolve EMA periods. If caller passed an explicit emaPeriods option,
  // respect that as-is (and bail UNKNOWN on insufficient input — don't
  // silently substitute defaults, that'd hide a misconfiguration). Only
  // fall back to DEFAULT_PERIODS when the caller passed nothing at all.
  let periods;
  if (opts.emaPeriods === undefined || opts.emaPeriods === null) {
    periods = DEFAULT_PERIODS;
  } else if (Array.isArray(opts.emaPeriods)) {
    periods = opts.emaPeriods
      .filter(p => Number.isFinite(p) && p > 0)
      .map(p => Math.floor(p));
  } else {
    periods = [];
  }
  if (periods.length < 2) return STANCE.UNKNOWN;

  // Need enough bars for the longest period to be warmed up.
  const maxPeriod = Math.max(...periods);
  if (bars.length < maxPeriod) return STANCE.UNKNOWN;

  const lastBar = bars[bars.length - 1];
  if (!lastBar) return STANCE.UNKNOWN;
  const close = Number(lastBar.close);
  if (!Number.isFinite(close)) return STANCE.UNKNOWN;

  // Compute EMAs; pull the last value of each.
  let series;
  try {
    series = computeEmas(bars, periods);
  } catch {
    return STANCE.UNKNOWN;
  }
  const lastIdx = bars.length - 1;

  // Sort periods ascending so shortest is first. The "stack" check walks
  // sortedValues asking: is each shorter EMA above (bullish) / below
  // (bearish) the next longer? Plus price must be on the same side of
  // the shortest EMA.
  //
  // computeEmas returns {period → [{time, value}, …]} — the value lives on
  // .value, not on the array element directly. A bar inside the warm-up
  // window has value === null, which we need to treat as "no usable EMA".
  const sortedPeriods = [...periods].sort((a, b) => a - b);
  const sortedValues = sortedPeriods.map(p => {
    const s = series?.[p];
    if (!Array.isArray(s)) return undefined;
    const point = s[lastIdx];
    return point && Number.isFinite(point.value) ? point.value : undefined;
  });
  if (!sortedValues.every(v => Number.isFinite(v))) return STANCE.UNKNOWN;

  let stackedUp = true;
  let stackedDown = true;
  for (let i = 0; i < sortedValues.length - 1; i++) {
    if (sortedValues[i] <= sortedValues[i + 1]) stackedUp = false;
    if (sortedValues[i] >= sortedValues[i + 1]) stackedDown = false;
  }

  const priceAboveShortest = close > sortedValues[0];
  const priceBelowShortest = close < sortedValues[0];

  if (stackedUp && priceAboveShortest) return STANCE.BULLISH;
  if (stackedDown && priceBelowShortest) return STANCE.BEARISH;
  return STANCE.MIXED;
};

export default classifyTimeframeStance;
